<template>
  <!-- One page still renders: a single-page range is previous + `1` + next, so a
       guard of "more than three links" hid the row entirely and a listing only
       showed a pager once it had grown past a page. The two ends stay on screen
       and disabled, which keeps the row's width steady instead of it appearing
       under the reader at the moment a second page exists. -->
  <nav v-if="items.length > 2" class="pagination" :aria-label="t('Pagination')">
    <ul class="pagination-list">
      <li v-for="item in items" :key="item.key">
        <Link
          v-if="item.url && !item.active"
          :href="item.url"
          class="pagination-link"
          :class="`pagination-link--${item.kind}`"
        >
          <font-awesome-icon
            v-if="item.kind === 'previous'"
            icon="arrow-left"
            aria-hidden="true"
          />
          <span>{{ item.label }}</span>
          <font-awesome-icon
            v-if="item.kind === 'next'"
            icon="arrow-right"
            aria-hidden="true"
          />
        </Link>

        <span
          v-else
          class="pagination-link"
          :class="[
            `pagination-link--${item.kind}`,
            {
              'is-active': item.active,
              'is-disabled': isDisabled(item)
            }
          ]"
          :aria-current="item.active ? 'page' : null"
          :aria-disabled="isDisabled(item) ? 'true' : null"
        >
          <font-awesome-icon
            v-if="item.kind === 'previous'"
            icon="arrow-left"
            aria-hidden="true"
          />
          <span v-if="item.kind !== 'gap'">{{ item.label }}</span>
          <span v-else aria-hidden="true">...</span>
          <font-awesome-icon
            v-if="item.kind === 'next'"
            icon="arrow-right"
            aria-hidden="true"
          />
        </span>
      </li>
    </ul>
  </nav>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* One pill row for every paged list on the site. The 2px edge is the same
   hairline the blog and promo cards use, so the pager sits in the card
   language rather than reading as a stock Bootstrap pager. */
.pagination {
  display: flex;
  justify-content: center;
  margin-top: 3rem;
}

.pagination-list {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  list-style: none;
  margin: 0;
  padding: 0;
}

.pagination-link {
  align-items: center;
  background: $ground;
  border: 2px solid $line-strong;
  border-radius: $r-sm;
  color: $text-heading;
  display: inline-flex;
  font-family: $headings-font-family;
  font-size: 0.85rem;
  font-weight: 600;
  gap: 0.45rem;
  justify-content: center;
  min-width: 2.9rem;
  padding: 0.6rem 1rem;
  text-decoration: none;
  transition: background $dur-quick $ease, border-color $dur-quick $ease, color $dur-quick $ease, transform $dur-quick $ease;

  svg {
    flex: 0 0 auto;
    height: 0.75rem;
    width: 0.75rem;
  }

  &:hover {
    border-color: $ring-focus;
    color: $primary;
    transform: translateY(-1px);
  }

  &:focus-visible {
    border-color: $primary;
    outline: 3px solid $ring-focus;
    outline-offset: 2px;
  }
}

/* Current page: filled, so it reads as the answer rather than a target. */
.pagination-link.is-active {
  background: $primary;
  border-color: $primary;
  color: $on-dark-strong;
  cursor: default;
}

/* The ends of the range while more pages exist. */
.pagination-link.is-disabled {
  background: $ground-tint;
  border-color: $line;
  color: $text-muted;
  cursor: not-allowed;
}

/* The elided span between page groups carries no frame of its own. */
.pagination-link--gap {
  background: none;
  border-color: transparent;
  color: $text-muted;
  min-width: 1.5rem;
  padding-left: 0.25rem;
  padding-right: 0.25rem;
}

.pagination-link--gap:hover {
  border-color: transparent;
  color: $text-muted;
  transform: none;
}

/* On narrow screens the Previous / Next words drop and the controls become
   icon pills, so a long range still fits one row. */
@media (max-width: 575.98px) {
  .pagination-link {
    border-radius: $r-sm;
    min-width: 2.6rem;
    padding: 0.5rem 0.7rem;
  }

  .pagination-link--previous span,
  .pagination-link--next span {
    display: none;
  }

  .pagination-link--previous svg,
  .pagination-link--next svg {
    height: 0.85rem;
    width: 0.85rem;
  }
}
</style>

<script>
import { Link } from '@inertiajs/inertia-vue3';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { useI18n } from 'vue-i18n';

FontAwesomeLibrary.add(faArrowLeft, faArrowRight);

export default {
  components: {
    Link
  },

  props: {
    /* The `links` array from a Laravel paginator: { url, label, active }. */
    links: {
      type: Array,
      required: true
    }
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          Next: 'Next',
          Pagination: 'Pagination',
          Previous: 'Previous'
        },
        'zh-MY': {
          Next: '下一页',
          Pagination: '分页',
          Previous: '上一页'
        },
        'ms-MY': {
          Next: 'Seterusnya',
          Pagination: 'Penomboran',
          Previous: 'Sebelumnya'
        }
      }
    });

    return { t };
  },

  computed: {
    /* The paginator hands back HTML-entity labels ("&laquo; Previous", "..."),
       so each link is classified once here and the template stays declarative.

       Laravel's `linkCollection()` always prepends the previous link and pushes
       the next one, and marks a collapsed run with the literal "...". The two
       ends are matched by position rather than by the English word, because the
       label itself is `__('pagination.previous')`: reading the word meant a
       missing or non-English `pagination` lang file left the raw key on screen
       and skipped the arrows. Position is the contract, so the words are free
       to be translated (or absent) without changing the pager. */
    items() {
      const lastIndex = this.links.length - 1;

      return this.links.map((link, index) => {
        const raw = (link.label || '').toString();
        const isGap = raw.indexOf('...') !== -1 || raw.indexOf('…') !== -1;

        let kind = 'page';

        if (isGap) {
          kind = 'gap';
        } else if (index === 0) {
          kind = 'previous';
        } else if (index === lastIndex) {
          kind = 'next';
        }

        let label = raw.replace(/[«»…]/g, '').trim();

        if (kind === 'gap') {
          label = '...';
        } else if (kind === 'previous') {
          label = this.t('Previous');
        } else if (kind === 'next') {
          label = this.t('Next');
        }

        return {
          key: `${kind}-${index}`,
          url: link.url,
          active: Boolean(link.active),
          kind,
          label
        };
      });
    }
  },

  methods: {
    /* The ends are the only items rendered as spans without a url that are still
       controls: the previous / next pill at the edge of the range stays on
       screen, greyed, so the row keeps its width. A gap also carries no url, but
       it is punctuation between two page groups, not a control, so it must not
       take the disabled treatment and be announced as a disabled item with a
       not-allowed cursor. */
    isDisabled(item) {
      return item.kind !== 'gap' && !item.url && !item.active;
    }
  }
};
</script>
